import { useSessionStorageState } from 'ahooks'

import { getConnectParams } from '../helpers/connectParams'

import { useSessionInfo } from './sessionInfo'

import { useAnalyticsData } from '@/hooks/analyticsData'
import { sha256 } from '@/helpers/sha256'


import { useRef, useEffect } from 'react';
const SignInTrackType = 'sign-in-track-type'

export function useTrackSignInOrSignUp({
  enabled = false,
}: {
  enabled?: boolean
}) {
  const { userId, isSignIn, trigger, email, userDateCreated } = useSessionInfo()
  const { trackSignUp, trackSignIn } = useAnalyticsData()

  const [trackType, setTrackType] = useSessionStorageState<
    'email' | 'Google' | '' | undefined
  >(SignInTrackType, { defaultValue: '' })

  const [trackOriginUrl, setTrackOriginUrl] = useSessionStorageState<
    string | undefined
  >('sign-in-track-origin-url', { defaultValue: '' })

  const trackRef = useRef<boolean>(false) // 保证只执行一次

  // 登录成功后上报日志
  // Log sign-in or sign-up actions after successful login
  useEffect(() => {
    setTimeout(async () => {
      if (trackRef.current) {
        return
      }
      trackRef.current = true

      // Ensure we have all necessary data before proceeding
      const dataLayer = (window as any)?.dataLayer as Record<string, any>[]
      let signInTrackType: '' | 'email' | 'Google' = ''
      try {
        signInTrackType = (sessionStorage?.getItem(SignInTrackType) || '') as
          | ''
          | 'email'
          | 'Google'
        signInTrackType = JSON.parse(signInTrackType)
      } catch (error) {
        signInTrackType = ''
      }

      if (
        !signInTrackType ||
        !userId ||
        !isSignIn ||
        !dataLayer ||
        !email ||
        !enabled
      ) {
        trackRef.current = false
        return
      }
      const emailSha256 = await sha256(email.toLowerCase())
      let signInTrackOriginUrl = ''

      try {
        signInTrackOriginUrl =
          sessionStorage?.getItem('sign-in-track-origin-url') || ''
        signInTrackOriginUrl = JSON.parse(signInTrackOriginUrl)
      } catch (error) {
        signInTrackOriginUrl = ''
      }

      let originUrl: string | null
      try {
        originUrl = new URL(signInTrackOriginUrl).searchParams.get('originUrl')
      } catch (error) {
        originUrl = ''
      }

      // Define a common payload structure for the tracking event
      const trackingPayload: {
        method: 'Google' | 'email'
        user_id: string
        origin_referrer?: string
        email_sha256: string
        origin_url: string
      } = {
        method: signInTrackType,
        user_id: userId,
        email_sha256: emailSha256,
        origin_url: originUrl || signInTrackOriginUrl || '',
      }

      const connectParams = getConnectParams()
      if (connectParams.redirect_uri) {
        trackingPayload.origin_referrer =
          process.env.NEXT_PUBLIC_BYPASSGPT_CHAT_OPEN_AI_URL || '/'
      }

      // Determine the event type and fire the tracking event
      if (signInTrackType === 'Google' && trigger === 'signUp') {
        trackSignUp({
          ...trackingPayload,
          timestamp: userDateCreated || new Date(),
        })
      } else {
        trackSignIn(trackingPayload)
      }

      // Reset trackType to its initial state if needed
      setTrackType(undefined)
      setTrackOriginUrl(undefined)
      trackRef.current = false
    }, 30)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignIn, userId, trigger, email, userDateCreated, enabled])

  return {
    setTrackType,
    setTrackOriginUrl,
  }
}
